import { createFileRoute, redirect } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout";
import { supabase } from "@/lib/supabase";
import { takePostAuthRedirect } from "@/lib/post-auth-redirect";

const GET_SESSION_SLOW_MS = 8000;

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ location }) => {
    // Leading indicator: warn (but don't abort) if getSession is abnormally
    // slow. A full hang is recovered by GlobalPending's stall net, which shows
    // the reload screen at LOAD_STALL_MS. A session logging this warning AND
    // then "app-load-stalled" is unambiguously the auth-getSession hang.
    const slowTimer = setTimeout(() => {
      Sentry.captureMessage("auth-getSession-slow", {
        level: "warning",
        tags: { feature: "auth", action: "startup-getSession" },
        extra: { thresholdMs: GET_SESSION_SLOW_MS },
      });
    }, GET_SESSION_SLOW_MS);

    const startedAt = performance.now();
    Sentry.addBreadcrumb({
      category: "boot",
      level: "info",
      message: "route-guard getSession start",
    });

    let result;
    try {
      result = await supabase.auth.getSession();
    } finally {
      clearTimeout(slowTimer);
    }

    Sentry.addBreadcrumb({
      category: "boot",
      level: "info",
      message: "route-guard getSession resolved",
      data: {
        ms: Math.round(performance.now() - startedAt),
        hasSession: !!result.data.session,
      },
    });

    if (!result.data.session) {
      throw redirect({
        to: "/sign-in",
        search: location.href === "/" ? {} : { redirect: location.href },
        replace: true,
      });
    }

    const postAuthRedirect = takePostAuthRedirect();
    if (postAuthRedirect) {
      throw redirect({ href: postAuthRedirect, replace: true });
    }
  },
  component: AuthenticatedLayout,
});
